import { RootState } from 'store/store';
import { useAppSelector, useAppStore, useAppDispatch } from 'store/store-hooks';
import { useLocation, Path } from 'components/_hooks/use-router';
import { useZoomCb } from 'components/app/_mediator/zoom/use-zoom-cb';
import { setRecentAction } from 'components/app/_mediator/app-logger.slice';
import { EMPTY_CELL, selectFileClips } from 'components/app/editor/_mediator/editor.slice';
import { useSelectorCb } from 'components/app/selector/_mediator/use-selector-cb';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { useFinalcutCb } from 'components/app/finalcuts/_mediator/use-finalcut-cb';
import { fcClipsInRange } from 'components/app/finalcuts/_mediator/fc-data.slice';

export type Direction = 'next' | 'prev' | 'last';

export const useClipsSwitch = () => {
  const { path } = useLocation();
  const { clipsMap, activeClip } = useAppSelector((state) => state.editor);
  const { srSelectClip } = useSelectorCb();
  const { fcSelectClip } = useFinalcutCb();
  const { sbSelectClip } = useSbCb();
  const { keepCursorInView } = useZoomCb();
  const clipsInRange = useAppSelector(fcClipsInRange);
  const clips = useAppSelector(selectFileClips);
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const goStoryboardNeighbor = (direction: Direction) => {
    const { editorStoryboard, sbPosition } = selectData(store.getState());
    const { clipId, cell } = getNeighborClip(
      direction,
      direction === 'last' ? 0 : sbPosition.cell,
      editorStoryboard[sbPosition.track]
    );
    sbSelectClip(clipId, cell, sbPosition.track);
    keepCursorInView();
  };

  const goFinalcutNeighbor = (direction: Direction) => {
    const { fcCell } = selectData(store.getState());
    const { cell } = getNeighborClip(direction, fcCell, clipsInRange);
    fcSelectClip(cell);
    keepCursorInView();
  };

  const handleNextClip = () => {
    dispatch(setRecentAction({ key: 'USER', desc: `select next clip` }));
    switch (path) {
      case Path.selector: {
        if (!clips || !clipsMap) return;
        let nextIndex = 0;
        if (activeClip) {
          const currIndex = clips.findIndex((clipId) => clipId === activeClip);
          if (currIndex !== -1 && currIndex < clips.length - 1) nextIndex = currIndex + 1;
        }
        srSelectClip(clips[nextIndex]);
        break;
      }
      case Path.storyboard:
        goStoryboardNeighbor('next');
        break;
      case Path.finalcuts:
        goFinalcutNeighbor('next');
        break;
      default:
        break;
    }
  };

  const handlePrevClip = () => {
    dispatch(setRecentAction({ key: 'USER', desc: `select prev clip` }));
    switch (path) {
      case Path.selector: {
        if (!clips || !clipsMap) return;
        let prevIndex = clips.length - 1;
        if (activeClip) {
          const currIndex = clips.findIndex((clipId) => clipId === activeClip);
          if (currIndex !== -1 && currIndex > 0) prevIndex = currIndex - 1;
        }
        srSelectClip(clips[prevIndex]);
        break;
      }
      case Path.storyboard:
        goStoryboardNeighbor('prev');
        break;
      case Path.finalcuts:
        goFinalcutNeighbor('prev');
        break;
      default:
        break;
    }
  };

  return { handleNextClip, handlePrevClip };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { storyBoard: editorStoryboard } = state.editor;
  const { sbPosition } = state.editor__controls;
  const { fcCell } = state.fc__controls;
  return { editorStoryboard, sbPosition, fcCell };
};

/* H E L P E R S */

const getNeighborClip = (direction: Direction, cursorCell: Index, track: CellClip[]) => {
  const len = track.length;
  let cell = cursorCell;
  let count = 0;
  let clipId = null;

  while (count < len) {
    if (direction === 'next') cell = cell < len - 1 ? ++cell : 0;
    else cell = cell > 0 ? --cell : len - 1;
    const trackClip = track[cell];
    if (trackClip === EMPTY_CELL) count++;
    else {
      clipId = trackClip;
      break;
    }
  }
  return { clipId, cell };
};
